/**
 * Warms the documentation chunks before the user commits to navigating.
 *
 * `App.tsx` splits the docs section into two `React.lazy` chunks (the shell and
 * the article), so a cold `/docs` navigation is a two-hop waterfall. React
 * Router wraps its state updates in `startTransition`, and React intentionally
 * does not swap in a Suspense fallback for a transition that suspends, so the
 * wait is invisible: the first click looks like it did nothing and users click
 * again.
 *
 * Importing both modules here fills the module registry ahead of the click, so
 * `React.lazy` resolves from cache in a microtask and the transition commits
 * without a visible stall. The chunks stay out of the entry bundle, which is
 * the reason they are lazy in the first place.
 *
 * The returned promise is memoised so repeated hovers cost nothing, and a
 * failed prefetch is swallowed: the real navigation still imports the module
 * and retries, so there is nothing to recover from here.
 */
type DocsPrefetch = () => void;

export type DocsPrefetchHandlers = {
  onMouseEnter: DocsPrefetch;
  onFocus: DocsPrefetch;
  onTouchStart: DocsPrefetch;
};

let docsPrefetch: Promise<unknown> | null = null;

function warmDocsChunks(): Promise<unknown> {
  // Kick both imports off in parallel rather than chaining them, so the shell
  // chunk does not have to resolve before the article chunk is requested.
  return Promise.all([
    import('@/components/docs/DocsShell'),
    import('@/pages/docs/DocsArticlePage'),
  ]);
}

/**
 * Idempotent prefetch of every documentation chunk. Safe to call on every
 * hover, focus, and touch of a documentation link.
 */
export function prefetchDocs(): void {
  if (docsPrefetch === null) {
    docsPrefetch = warmDocsChunks().catch(() => {
      // Let a later attempt retry rather than caching the rejection.
      docsPrefetch = null;
    });
  }
}

/**
 * Handlers to spread onto a documentation link. Hover and focus are the two
 * moments a user signals intent to navigate; touch matters on devices where a
 * tap is preceded by neither.
 */
export function docsPrefetchHandlers(): DocsPrefetchHandlers {
  return {
    onMouseEnter: prefetchDocs,
    onFocus: prefetchDocs,
    onTouchStart: prefetchDocs,
  };
}